<template>
  <component
    :is="component"
    :class="className"
    :href="bookUrl"
    @click="onCardClick"
    :id="book.slug">
    <figure
      class="book-card-mini__figure">
      <pict
        block-name="book-card-mini"
        :sources="book.image.sources"
        :default="book.image.default"
        :alt="altText" />
    </figure>
    <div class="book-card-mini__content">
      <h4
        class="book-card-mini__headline"
        v-html="book.title" />
      <div
        class="book-card-mini__author"
        v-html="book.author" />
      <div
        class="book-card-mini__subheadline"
        v-if="showDescription"
        v-html="book.subtitle" />
      <div
        class="book-card-mini__duration"
        v-html="$t('duration', { duration: book.readingDuration })" />

      <div
        class="book-card-mini__preview-link"
        v-if="showPreviewLink">
        <book-preview-link
          :preview-url="book.audioPreviewUrl" />
      </div>
    </div>
  </component>
</template>

<script>
import Pict from "@/shared/pict";
import events from "@/events";
import BookPreviewLink from "./book-preview-link";

export default {
  name: "BookCardMini",
  components: {
    Pict,
    BookPreviewLink
  },
  props: {
    book: { type: Object, default: () => {} },
    showPreviewLink: { type: Boolean, default: false },
    modifiers: { type: Array, default: () => [] },
    isBookLinked: { type: Boolean, default: false },
    showDescription: { type: Boolean, default: false }
  },
  computed: {
    className() {
      const classes = {
        "book-card-mini": true,
        "book-card-mini--linked": this.bookUrl
      };

      this.modifiers.forEach(modifier => {
        classes[`book-card-mini--${modifier}`] = true;
      });

      return classes;
    },
    component() {
      return this.bookUrl ? "a" : "div";
    },
    bookUrl() {
      return this.isBookLinked && this.book.previewUrl;
    },
    altText() {
      return this.$t("altText", { title: this.book.title, author: this.book.author });
    }
  },
  methods: {
    onCardClick() {
      this.$emit("click", this.book.slug);
      events.track("body-20210820-book_card", "1-click", this.book.slug);
      if (this.bookUrl && this.$router) {
        this.$router.assign(this.previewUrl);
      }
    }
  },
  i18n: {
    messages: {
      en: {
        duration: "{duration} min",
        audio: "Audio available",
        altText: "Blinks cover of \"{title}\" by {author}"
      },
      de: {
        duration: "{duration} min",
        audio: "Audio-Version verfügbar",
        altText: "Blinks-Cover des Buchs \"{title}\" von {author}"
      }
    }
  }
};
</script>

<style lang="scss">
@import "@/styles/helpers/ellipsis";

.book-card-mini {
  $root: &;

  @include font-family(sans-serif);

  position: relative;

  display: flex;
  flex-grow: 1;
  flex-direction: column;
  width: 100%;

  #{&} {

    &__figure {
      position: relative;

      margin-bottom: $spacing-8;
      overflow: hidden;

      border-radius: $border-radius-4;
    }

    &__picture {
      position: relative;

      display: block;
      width: 100%;
      padding-top: 100%;
    }

    &__image {
      position: absolute;
      top: 0;

      display: block;
      width: 100%;
      height: 100%;

      object-fit: cover;
    }

    &__content {
      flex-grow: 1;
      width: 100%;
    }

    &__headline {
      @include font-style(h5);
      @include ellipsis($lines: 3);

      position: relative;

      width: 100%;
      margin-top: 0;
      margin-bottom: 0;
    }

    &__subheadline {
      @include font-style(caption, $color: $black);
      @include ellipsis($lines: 3);
    }

    &__author {
      @include font-style(caption);
      @include ellipsis($lines: 1);

      margin-bottom: $spacing-8;
    }

    &__duration {
      @include font-style(caption);
    }

    &__preview-link {
      margin-top: $spacing-8;
    }
  }

  &--small {
    width: auto;
  }

  &--linked {
    cursor: pointer;

    &:hover {
      opacity: 0.9;
    }
  }

  &--dark {
    #{$root}__headline {
      color: $white;
    }

    #{$root}__author {
      color: $light-grey;
    }

    #{$root}__subheadline {
      color: $lightest-grey;
    }

    #{$root}__duration {
      color: $light-grey;
    }
  }
}
</style>
